import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, apiService } from '../services/api';
import { 
  X, 
  Send, 
  Loader2, 
  Sparkles,
  BarChart3,
  PieChart,
  LineChart,
  Table,
  FileSearch,
  ChevronDown,
  Check,
  Plus,
  Minimize2,
  Maximize2
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const WillFloatingChat = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [extractions, setExtractions] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState('');
  const [conversation, setConversation] = useState([]);
  const [lastResponse, setLastResponse] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch connectors and extractions
  useEffect(() => {
    if (token && isOpen) {
      fetchData();
    }
  }, [token, isOpen]);

  const fetchData = async () => {
    try {
      const [connectorsRes, extractionsRes] = await Promise.all([
        apiService.getConnectors(token),
        apiService.getExtractions(token)
      ]);
      setConnectors(connectorsRes.data);
      setExtractions(extractionsRes.data);
      
      // Auto-select first connector if available
      if (connectorsRes.data.length > 0 && !selectedConnector) {
        setSelectedConnector(connectorsRes.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const handleSend = async () => {
    if (!message.trim()) return;
    
    // Add WILL prefix if not present
    let finalMessage = message.trim();
    if (!finalMessage.toUpperCase().startsWith('WILL ')) {
      finalMessage = 'WILL ' + finalMessage;
    }

    if (!selectedConnector) {
      toast.error('Selecione um conector primeiro');
      return;
    }

    // Add user message to conversation
    const userMsg = { role: 'user', content: message.trim(), timestamp: new Date() };
    setConversation(prev => [...prev, userMsg]);
    setMessage('');
    setLoading(true);

    try {
      const result = await apiService.askWill(selectedConnector, finalMessage, {
        extractions: extractions.map(e => ({ id: e.id, name: e.name }))
      }, token);
      
      setLastResponse(result.data);
      
      // Build assistant response
      let assistantContent = '';
      
      if (result.data.warnings?.length > 0) {
        assistantContent += `⚠️ ${result.data.warnings.join('\n')}\n\n`;
      }
      
      if (result.data.preview?.length > 0) {
        assistantContent += `📊 Encontrei ${result.data.preview.length} registros!\n\n`;
        
        // Show sample data
        const sample = result.data.preview.slice(0, 3);
        sample.forEach((row, i) => {
          const values = Object.entries(row).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(', ');
          assistantContent += `• ${values}...\n`;
        });
        
        if (result.data.preview.length > 3) {
          assistantContent += `\n... e mais ${result.data.preview.length - 3} registros\n`;
        }
      }
      
      if (result.data.suggested_widgets?.length > 0) {
        assistantContent += `\n💡 Sugestões de visualização:\n`;
        result.data.suggested_widgets.forEach(w => {
          const icon = w.type === 'pie' ? '🥧' : w.type === 'bar' ? '📊' : w.type === 'line' ? '📈' : '📋';
          assistantContent += `${icon} ${w.title} (${w.type})\n`;
        });
      }

      if (!assistantContent) {
        assistantContent = 'Não consegui processar sua solicitação. Tente ser mais específico sobre o que deseja visualizar.';
      }

      const assistantMsg = { 
        role: 'assistant', 
        content: assistantContent, 
        timestamp: new Date(),
        data: result.data
      };
      setConversation(prev => [...prev, assistantMsg]);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao processar solicitação';
      setConversation(prev => [...prev, { 
        role: 'assistant', 
        content: `❌ ${errorMsg}`, 
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDashboard = async () => {
    if (!lastResponse?.builder_spec_json || !lastResponse?.suggested_widgets?.length) {
      toast.error('Gere uma consulta primeiro');
      return;
    }

    try {
      // Create extraction first
      const extractionRes = await apiService.createExtraction({
        name: `Extração WILL - ${new Date().toLocaleString('pt-BR')}`,
        description: conversation.find(m => m.role === 'user')?.content || '',
        connector_id: selectedConnector,
        builder_spec_json: lastResponse.builder_spec_json
      }, token);

      // Create dashboard
      const dashRes = await apiService.createDashboard({
        name: `Dashboard WILL - ${new Date().toLocaleString('pt-BR')}`,
        layout_json: {}
      }, token);

      // Create widgets from suggestions
      for (const widget of lastResponse.suggested_widgets) {
        await apiService.createWidget(dashRes.data.id, {
          title: widget.title,
          type: widget.type,
          extraction_id: extractionRes.data.id,
          config_json: widget.config || {},
          filters_json: {},
          refresh_seconds: 300
        }, token);
      }

      toast.success('Dashboard criado com sucesso!');
      
      // Add confirmation to conversation
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: `✅ Dashboard criado! Clique no botão abaixo para visualizar.`,
        timestamp: new Date(),
        dashboardId: dashRes.data.id
      }]);

      setLastResponse(null);
      
    } catch (error) {
      toast.error('Erro ao criar dashboard');
    }
  };

  const handleSaveExtraction = async () => {
    if (!lastResponse?.builder_spec_json) {
      toast.error('Gere uma consulta primeiro');
      return;
    }

    try {
      await apiService.createExtraction({
        name: `Extração WILL - ${new Date().toLocaleString('pt-BR')}`,
        description: conversation.find(m => m.role === 'user')?.content || '',
        connector_id: selectedConnector,
        builder_spec_json: lastResponse.builder_spec_json
      }, token);

      toast.success('Extração salva!');
      fetchData(); // Refresh extractions list
      
    } catch (error) {
      toast.error('Erro ao salvar extração');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickActions = [
    { label: 'Top 10 vendas', prompt: 'mostre os 10 produtos mais vendidos' },
    { label: 'Vendas por mês', prompt: 'total de vendas agrupado por mês' },
    { label: 'Clientes ativos', prompt: 'lista dos clientes com mais pedidos' },
  ];

  if (!user) return null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
          data-testid="will-floating-btn"
        >
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 blur-lg opacity-70 group-hover:opacity-100 transition-opacity animate-pulse" />
            
            {/* Button */}
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-300">
              {/* Avatar/Icon */}
              <div className="relative">
                <Sparkles className="w-7 h-7 text-white" />
                {/* Animated ring */}
                <div className="absolute -inset-2 rounded-full border-2 border-white/30 animate-ping" style={{ animationDuration: '2s' }} />
              </div>
            </div>
            
            {/* Label */}
            <div className="absolute -top-2 -right-2 bg-white rounded-full px-2 py-0.5 shadow-lg">
              <span className="text-xs font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                WILL
              </span>
            </div>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed z-50 bg-white shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 flex flex-col ${
            isMinimized 
              ? 'bottom-4 right-4 sm:bottom-6 sm:right-6 w-72 h-16 rounded-2xl' 
              : 'bottom-2 right-2 left-2 sm:left-auto sm:bottom-6 sm:right-6 sm:w-[420px] h-[85vh] sm:h-[600px] rounded-2xl'
          }`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm">WILL</h3>
                <p className="text-white/70 text-xs">Assistente Comercial IA</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isMinimized ? <Maximize2 className="w-4 h-4 text-white" /> : <Minimize2 className="w-4 h-4 text-white" />}
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Connector Selector */}
              <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 shrink-0">
                <Select value={selectedConnector} onValueChange={setSelectedConnector}>
                  <SelectTrigger className="h-8 text-xs bg-white">
                    <SelectValue placeholder="Selecione o banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {connectors.map(conn => (
                      <SelectItem key={conn.id} value={conn.id} className="text-xs">
                        {conn.name} {conn.selected_tables?.length ? `(${conn.selected_tables.length} tabelas)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {conversation.length === 0 && (
                  <div className="text-center py-4 sm:py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-violet-500" />
                    </div>
                    <h4 className="font-semibold text-slate-800 mb-2">Olá! Sou o WILL</h4>
                    <p className="text-sm text-slate-500 mb-4 px-2">
                      Seu assistente comercial especialista em dashboards e relatórios. 
                      Como posso ajudar?
                    </p>
                    
                    {/* Quick actions */}
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400 mb-2">Sugestões rápidas:</p>
                      {quickActions.map((action, i) => (
                        <button
                          key={i}
                          onClick={() => setMessage(action.prompt)}
                          className="block w-full text-left px-3 py-2 text-sm bg-slate-50 hover:bg-violet-50 rounded-lg transition-colors text-slate-600 hover:text-violet-600"
                        >
                          💡 {action.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {conversation.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[90%] sm:max-w-[85%] ${
                      msg.role === 'user' 
                        ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-2xl rounded-br-md' 
                        : msg.isError 
                          ? 'bg-red-50 text-red-700 rounded-2xl rounded-bl-md border border-red-200'
                          : 'bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md'
                    } px-4 py-3 shadow-sm`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      
                      {/* Action buttons for assistant with data */}
                      {msg.role === 'assistant' && msg.data?.builder_spec_json && (
                        <div className="mt-3 pt-3 border-t border-slate-200/50 flex flex-col sm:flex-row flex-wrap gap-2">
                          <button
                            onClick={handleCreateDashboard}
                            className="flex items-center justify-center gap-1 px-3 py-2 sm:py-1.5 bg-violet-600 text-white text-xs rounded-lg hover:bg-violet-700 transition-colors w-full sm:w-auto"
                          >
                            <BarChart3 className="w-3 h-3 shrink-0" />
                            Criar Dashboard
                          </button>
                          <button
                            onClick={handleSaveExtraction}
                            className="flex items-center justify-center gap-1 px-3 py-2 sm:py-1.5 bg-white text-violet-600 text-xs rounded-lg hover:bg-violet-50 transition-colors border border-violet-200 w-full sm:w-auto"
                          >
                            <FileSearch className="w-3 h-3 shrink-0" />
                            Salvar Extração
                          </button>
                        </div>
                      )}

                      {/* Dashboard link */}
                      {msg.dashboardId && (
                        <button
                          onClick={() => {
                            setIsOpen(false);
                            navigate(`/dashboard/${msg.dashboardId}`);
                          }}
                          className="mt-2 w-full flex justify-center items-center gap-1 px-3 py-2 bg-emerald-600 text-white text-sm sm:text-xs rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          <BarChart3 className="w-4 h-4 sm:w-3 sm:h-3 shrink-0" />
                          Ver Dashboard
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-xs text-slate-500">Analisando...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="shrink-0 p-3 sm:p-4 bg-white border-t border-slate-100">
                <div className="flex items-end gap-2">
                  <div className="flex-1 relative">
                    <textarea
                      ref={inputRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ex: mostre vendas por produto..."
                      className="w-full px-4 py-3 pr-2 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 text-sm"
                      rows={1}
                      style={{ minHeight: '48px', maxHeight: '120px' }}
                      data-testid="will-chat-input"
                    />
                  </div>
                  <button
                    onClick={handleSend}
                    disabled={loading || !message.trim()}
                    className="shrink-0 p-3 sm:p-4 mb-[1px] bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl hover:from-violet-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    data-testid="will-chat-send"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <p className="text-[10px] sm:text-xs text-slate-400 mt-2 text-center">
                  Prefixo "WILL" é adicionado automaticamente
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default WillFloatingChat;
